import { Trophy, Flag, Target } from "lucide-react"
import { colors } from "../../styles/theme"
import { Event, EventCategory } from "./types"
import DragRacingTournamentManager from "./DragRacingTournamentManager"

interface BracketsContentProps {
  selectedEvent: string
  selectedCategory: string
  getSelectedEvent: () => Event | undefined
  getSelectedCategory: () => EventCategory | undefined
  getBracketStatusColor: (status: string) => string
  getBracketStatusLabel: (status: string) => string
  onBracketCreated: (bracketId: string) => void
}

export function BracketsContent({ 
  selectedEvent, 
  selectedCategory, 
  getSelectedEvent, 
  getSelectedCategory, 
  getBracketStatusColor, 
  getBracketStatusLabel,
  onBracketCreated 
}: BracketsContentProps) {
  if (!selectedEvent) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-12 w-12 mx-auto mb-4" style={{ color: colors.text.tertiary }} />
        <h3 className="text-lg font-medium mb-2" style={{ color: colors.text.primary }}>
          Select a Tournament Event
        </h3>
        <p style={{ color: colors.text.secondary }}>
          Choose an event from the sidebar to manage elimination brackets
        </p>
      </div>
    )
  }

  if (!selectedCategory) {
    return (
      <div className="text-center py-12">
        <Flag className="h-12 w-12 mx-auto mb-4" style={{ color: colors.text.tertiary }} />
        <h3 className="text-lg font-medium mb-2" style={{ color: colors.text.primary }}>
          Select a Racing Category
        </h3>
        <p style={{ color: colors.text.secondary }}>
          Choose a category to view and manage tournament brackets
        </p>
      </div>
    )
  }

  return (
    <div>
      {/* Event/Category Header */}
      <div className="mb-6 pb-6 border-b" style={{ borderColor: colors.border.primary }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: colors.text.primary }}>
              {getSelectedEvent()?.name}
            </h2>
            <p className="text-sm mt-1" style={{ color: colors.text.secondary }}>
              {getSelectedCategory()?.name}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-medium" style={{ color: colors.text.primary }}>
                {getSelectedCategory()?.prize_pool}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                Prize Pool
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium" style={{ color: colors.text.primary }}>
                {getSelectedCategory()?.participants}/{getSelectedCategory()?.max_participants}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                Participants
              </div>
            </div>
          </div>
        </div>

        {/* Bracket Status */}
        {(() => {
          const category = getSelectedCategory()
          return category ? (
            <div className="flex items-center justify-between">
              <div
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${getBracketStatusColor(category.bracket_status)}`}
              >
                <Target className="h-4 w-4" />
                {getBracketStatusLabel(category.bracket_status)}
              </div>
              {category.bracket_status !== "not_started" && (
                <div className="text-sm" style={{ color: colors.text.secondary }}>
                  Round {category.current_round} of {category.total_rounds}
                </div>
              )}
            </div>
          ) : null
        })()}
      </div>
      
      {/* Tournament Manager Component */}
      <DragRacingTournamentManager
        eventId={selectedEvent}
        categoryId={selectedCategory}
        categoryName={getSelectedCategory()?.name || ""}
        onBracketCreated={onBracketCreated}
      />
    </div>
  )
} 